import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { api, mutate } from '../shared/api';
import { adminSectionDefinition, adminSectionNavLabel } from '../shared/adminSectionRegistry';
import { matchesQuickSearch, QuickSearchInput } from '../shared/quickSearch';
import { AdminCreateCard } from '../shared/AdminCreateCard';
import { MultiSelectDropdown } from '../shared/MultiSelectDropdown';

type Row = {
    id: number;
    company_id: number;
    code: string;
    name: string;
    deleted_at?: string | null;
};

type Payload = {
    rows: Row[];
    companies: Array<{ id: number; code: string; name: string }>;
    departments?: Array<{ id: number; company_id: number; code: string; name: string }>;
    project_departments?: Array<{ project_id: number; department_id: number }>;
};

export function AdminE2ProjectsSection({
    token,
    locale,
    reloadTick,
}: {
    token: string;
    locale: 'hr' | 'en';
    reloadTick: number;
}) {
    const def = adminSectionDefinition('127');
    const title = def ? adminSectionNavLabel(def, locale) : '127';
    const [payload, setPayload] = useState<Payload | null>(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [tableSearch, setTableSearch] = useState('');
    const [companyFilter, setCompanyFilter] = useState<Array<number | string>>([]);
    const [statusFilter, setStatusFilter] = useState<'active' | 'archived' | 'all'>('active');

    const [nCompanyId, setNCompanyId] = useState<string>('');
    const [nCode, setNCode] = useState('');
    const [nName, setNName] = useState('');

    const [nDeptIds, setNDeptIds] = useState<Array<number | string>>([]);

    const [editId, setEditId] = useState<number | null>(null);
    const [eCode, setECode] = useState('');
    const [eName, setEName] = useState('');
    const [eDeptIds, setEDeptIds] = useState<Array<number | string>>([]);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await api<Payload>('/admin/e2/projects', token);
            setPayload(res);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'API');
            setPayload(null);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        void load();
    }, [load, reloadTick]);

    const rows = payload?.rows ?? [];

    const filteredRows = useMemo(() => {
        const allow = new Set(companyFilter.map((v) => Number(v)));
        return rows.filter((r) => {
            if (allow.size > 0 && !allow.has(Number(r.company_id))) return false;
            if (statusFilter === 'active' && r.deleted_at) return false;
            if (statusFilter === 'archived' && !r.deleted_at) return false;
            return matchesQuickSearch(
                [
                    r.id,
                    r.code,
                    r.name,
                    payload?.companies.find((c) => c.id === r.company_id)?.code,
                    payload?.companies.find((c) => c.id === r.company_id)?.name,
                ],
                tableSearch,
            );
        });
    }, [rows, tableSearch, companyFilter, statusFilter, payload?.companies]);

    async function submitNew(ev: FormEvent) {
        ev.preventDefault();
        if (!nCompanyId || !nCode.trim() || !nName.trim()) return;
        try {
            await mutate('/admin/e2/projects', token, 'POST', {
                company_id: Number(nCompanyId),
                code: nCode.trim(),
                name: nName.trim(),
                department_ids: nDeptIds.map((v) => Number(v)),
            });
            setNCompanyId('');
            setNCode('');
            setNName('');
            setNDeptIds([]);
            await load();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'API');
        }
    }

    function startEdit(row: Row) {
        setEditId(row.id);
        setECode(row.code);
        setEName(row.name);
        const linked = (payload?.project_departments ?? [])
            .filter((pd) => Number(pd.project_id) === Number(row.id))
            .map((pd) => Number(pd.department_id));
        setEDeptIds(linked);
    }

    async function saveEdit(ev: FormEvent) {
        ev.preventDefault();
        if (!editId) return;
        try {
            await mutate(`/admin/e2/projects/${editId}`, token, 'PATCH', {
                code: eCode.trim(),
                name: eName.trim(),
                department_ids: eDeptIds.map((v) => Number(v)),
            });
            setEditId(null);
            await load();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'API');
        }
    }

    async function archiveRow(id: number) {
        try {
            await mutate(`/admin/e2/projects/${id}/archive`, token, 'PATCH', {});
            await load();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'API');
        }
    }

    return (
        <section className="card admin-block">
            <div className="bi-module-hero">
                <div className="bi-module-hero-icon">📁</div>
                <div className="bi-module-hero-text">
                    <h1>{title}</h1>
                    <p className="muted">
                        {locale === 'en'
                            ? 'Company projects for cost routing (E2). Required context for purchase-order codes (125).'
                            : 'Projekti tvrtki za troškovno usmjeravanje (E2). Kontekst za šifre narudžbe (125).'}
                    </p>
                </div>
            </div>
            {error ? <p className="error">{error}</p> : null}
            {loading ? <p className="muted">{locale === 'en' ? 'Loading…' : 'Učitavam…'}</p> : null}
            {!loading && payload ? (
                <>
                    <AdminCreateCard
                        title={locale === 'en' ? 'New project' : 'Novi projekt'}
                        hint={locale === 'en' ? 'Code is short identifier; name is the full title.' : 'Šifra je kratki identifikator; naziv je puni opis.'}
                        locale={locale}
                    >
                        <form onSubmit={submitNew}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(14rem, 1fr))', gap: '0.75rem', maxWidth: '52rem' }}>
                                <label className="filter-label">
                                    {locale === 'en' ? 'Company' : 'Tvrtka'}
                                    <select value={nCompanyId} onChange={(ev) => setNCompanyId(ev.target.value)} required>
                                        <option value="">{locale === 'en' ? 'Select' : 'Odaberi'}</option>
                                        {payload.companies.map((c) => (
                                            <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="filter-label">
                                    {locale === 'en' ? 'Code' : 'Šifra'}
                                    <input value={nCode} onChange={(ev) => setNCode(ev.target.value)} required />
                                </label>
                                <label className="filter-label" style={{ gridColumn: '1 / -1' }}>
                                    {locale === 'en' ? 'Name' : 'Naziv'}
                                    <input value={nName} onChange={(ev) => setNName(ev.target.value)} required />
                                </label>
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <label className="filter-label" style={{ display: 'block' }}>
                                        {locale === 'en' ? 'Departments (multi-select)' : 'Odjeli (više)'}
                                    </label>
                                    <MultiSelectDropdown
                                        locale={locale}
                                        options={(payload.departments ?? [])
                                            .filter((d) => !nCompanyId || Number(d.company_id) === Number(nCompanyId))
                                            .map((d) => ({ value: d.id, label: `${d.code ? d.code + ' — ' : ''}${d.name}` }))}
                                        selected={nDeptIds}
                                        onChange={setNDeptIds}
                                        emptyLabel={locale === 'en' ? 'No departments linked' : 'Bez povezanih odjela'}
                                    />
                                    <p className="muted" style={{ marginTop: 6, fontSize: '0.78rem' }}>
                                        {locale === 'en' ? 'Required for purchase-order code generation in module 125.' : 'Potrebno za generiranje šifre narudžbe (modul 125).'}
                                    </p>
                                </div>
                            </div>
                            <div className="admin-toolbar" style={{ marginTop: '0.75rem' }}>
                                <button type="submit" className="compact">{locale === 'en' ? 'Create' : 'Kreiraj'}</button>
                            </div>
                        </form>
                    </AdminCreateCard>

                    <div className="admin-toolbar" style={{ marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                        <span className="muted">{locale === 'en' ? 'Status' : 'Status'}:</span>
                        {(['active', 'archived', 'all'] as const).map((s) => (
                            <button
                                key={s}
                                type="button"
                                className={statusFilter === s ? 'compact' : 'secondary compact'}
                                onClick={() => setStatusFilter(s)}
                            >
                                {s}
                            </button>
                        ))}
                        <span className="muted" style={{ marginLeft: '0.75rem' }}>{locale === 'en' ? 'Company' : 'Tvrtka'}:</span>
                        <div style={{ minWidth: '14rem' }}>
                            <MultiSelectDropdown
                                locale={locale}
                                options={payload.companies.map((c) => ({ value: c.id, label: `${c.code} — ${c.name}` }))}
                                selected={companyFilter}
                                onChange={setCompanyFilter}
                                emptyLabel={locale === 'en' ? 'All companies' : 'Sve tvrtke'}
                            />
                        </div>
                        <QuickSearchInput
                            value={tableSearch}
                            onChange={setTableSearch}
                            placeholder={locale === 'en' ? 'Search projects…' : 'Pretraga projekata…'}
                        />
                    </div>
                    <div className="table-wrap">
                        <table className="admin-user-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>{locale === 'en' ? 'Company' : 'Tvrtka'}</th>
                                    <th>{locale === 'en' ? 'Code' : 'Šifra'}</th>
                                    <th>{locale === 'en' ? 'Name' : 'Naziv'}</th>
                                    <th>{locale === 'en' ? 'Departments' : 'Odjeli'}</th>
                                    <th>{locale === 'en' ? 'Status' : 'Status'}</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRows.map((r) => {
                                    const co = payload.companies.find((c) => Number(c.id) === Number(r.company_id));
                                    const deptIds = (payload.project_departments ?? [])
                                        .filter((pd) => Number(pd.project_id) === Number(r.id))
                                        .map((pd) => Number(pd.department_id));
                                    const deptLabels = deptIds
                                        .map((id) => (payload.departments ?? []).find((d) => Number(d.id) === id))
                                        .filter((d): d is NonNullable<typeof d> => Boolean(d))
                                        .map((d) => d.code || d.name)
                                        .join(', ');
                                    return (
                                        <tr key={r.id}>
                                            <td>{r.id}</td>
                                            <td>{co ? `${co.code} — ${co.name}` : `#${r.company_id}`}</td>
                                            <td>{r.code}</td>
                                            <td>{r.name}</td>
                                            <td className="muted" style={{ fontSize: '0.85rem' }}>{deptLabels || '—'}</td>
                                            <td>{r.deleted_at ? (locale === 'en' ? 'Archived' : 'Arhiviran') : (locale === 'en' ? 'Active' : 'Aktivan')}</td>
                                            <td className="admin-toolbar">
                                                <button type="button" className="secondary compact" onClick={() => startEdit(r)}>{locale === 'en' ? 'Edit' : 'Uredi'}</button>
                                                <button type="button" className="secondary compact" onClick={() => void archiveRow(r.id)}>
                                                    {r.deleted_at ? (locale === 'en' ? 'Restore' : 'Vrati') : (locale === 'en' ? 'Archive' : 'Arhiviraj')}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {filteredRows.length === 0 ? (
                            <p className="muted">{locale === 'en' ? 'No rows.' : 'Nema zapisa.'}</p>
                        ) : null}
                    </div>

                    {editId ? (
                        <form className="admin-section-card" style={{ marginTop: '1rem' }} onSubmit={saveEdit}>
                            <h4>{locale === 'en' ? `Edit project #${editId}` : `Uredi projekt #${editId}`}</h4>
                            <label className="filter-label">
                                {locale === 'en' ? 'Code' : 'Šifra'}
                                <input value={eCode} onChange={(ev) => setECode(ev.target.value)} required />
                            </label>
                            <label className="filter-label">
                                {locale === 'en' ? 'Name' : 'Naziv'}
                                <input value={eName} onChange={(ev) => setEName(ev.target.value)} required />
                            </label>
                            <label className="filter-label" style={{ display: 'block' }}>
                                {locale === 'en' ? 'Departments' : 'Odjeli'}
                            </label>
                            {(() => {
                                const row = (payload?.rows ?? []).find((r) => r.id === editId);
                                const co = row?.company_id;
                                return (
                                    <MultiSelectDropdown
                                        locale={locale}
                                        options={(payload?.departments ?? [])
                                            .filter((d) => co == null || Number(d.company_id) === Number(co))
                                            .map((d) => ({ value: d.id, label: `${d.code ? d.code + ' — ' : ''}${d.name}` }))}
                                        selected={eDeptIds}
                                        onChange={setEDeptIds}
                                        emptyLabel={locale === 'en' ? 'No departments linked' : 'Bez povezanih odjela'}
                                    />
                                );
                            })()}
                            <div className="admin-toolbar">
                                <button type="submit" className="compact">{locale === 'en' ? 'Save' : 'Spremi'}</button>
                                <button type="button" className="secondary compact" onClick={() => setEditId(null)}>{locale === 'en' ? 'Cancel' : 'Odustani'}</button>
                            </div>
                        </form>
                    ) : null}
                </>
            ) : null}
        </section>
    );
}
